import React, { useState, useEffect, FC } from 'react';
import styled from '@emotion/styled';
import { useQuery, gql, useMutation } from '@apollo/client';
import { CountAndAmount, SerializedStorage as ZReport } from '@baemingo/report-events/dist/types';
// @ts-ignore
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

import {
  useSelector,
  useI18n,
  useCashRegister,
  useLogger,
  useCurrency,
  useDates,
  processCashEvents,
  useButton,
  CashRegisterTotals,
  useLicenses,
  useMetadata,
} from '~Hooks';

import '~Assets/Css/custom-calendar-styles.css';
import Toggle from '~Components/Toggle';
import navigateLeftIcon from '~/Assets/icons//navigate-left.svg';
import navigateRightIcon from '~/Assets/icons//navigate-right.svg';

import {
  GetAllCashEvents,
  GetAllCashEventsVariables,
  GetVenueTableEventsSnapshots,
  GetVenueTableEventsSnapshotsVariables,
} from './__queries__';
import { GetZReports, GetZReportsVariables } from './__queries__/GetZReports';
import {
  GetDeviceReportSnapshot,
  GetDeviceReportSnapshotVariables,
} from './__queries__/GetDeviceReportSnapshot';
import { Languages } from '~Types';
import printNorwayZReport from './Printed/Norway';
import printGeneralZReport from './Printed/General';
import NorwayView from './View/Norway';
import GeneralView from './View/General';
import { DateTime } from 'luxon';
import { Box } from '~Styles';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Cheque } from '~Scenes/Reports';
import { PARKED_ORDER_TABLE_ID } from '~Scenes/ParkedOrders/consts';
import { useStorage } from '~Components/Storage';
import DateInput from '~ComponentsImproved/DateInput';
import { handyBreakpoint } from '~Styles/breakpoints';
import { SendZReportEmail } from './Components/SendZReportEmail';
import useBongQuery from '~Scenes/POS/Queries/query';

export type ExtendedReport = ZReport & { isZReport: boolean; date?: string };

const Z_REPORT_QUERY = gql`
  query GetZReports($venueId: String!, $date: DateTime!, $deviceName: String, $timezone: String) {
    getZReports(venueId: $venueId, deviceName: $deviceName, date: $date, timezone: $timezone) {
      success
      message
      error
      reports
    }
  }
`;

const DEVICE_REPORT_SNAPSHOT_QUERY = gql`
  query GetDeviceReportSnapshot($venueId: String!, $deviceName: String!, $date: DateTime!) {
    reportDeviceSnapshot(venueId: $venueId, deviceName: $deviceName, date: $date) {
      snapshot
      isPartial
    }
  }
`;

const CONFIGURE_VENUE_DEVICE = gql`
  mutation ConfigureVenueDevice($venueId: String!, $companyId: String!, $deviceName: String!) {
    configureVenueDevice(venueId: $venueId, companyId: $companyId, deviceName: $deviceName) {
      success
      message
      error
    }
  }
`;

const ALL_CASH_EVENTS_QUERY = gql`
  query GetAllCashEvents(
    $filter: POSDeviceWhereInput
    $sentAfter: DateTime!
    $sentBefore: DateTime
    $venueId: String
  ) {
    allPOSDevices(filter: $filter) {
      id
      cashEvents {
        events(sentAfter: $sentAfter, sentBefore: $sentBefore, venueId: $venueId) {
          items
          lastKey
        }
      }
    }
  }
`;

const TABLE_EVENTS_SNAPSHOTS_QUERY = gql`
  query GetVenueTableEventsSnapshots($venueId: ID!, $date: DateTime!) {
    Venue(id: $venueId) {
      id
      eventsSnapshot(date: $date)
    }
  }
`;

const CalendarWrapper = styled.div`
  padding: 16px;
  width: 350px;
  box-sizing: border-box;

  ${handyBreakpoint} {
    width: 100%;
  }
`;

const Prev = styled.div`
  width: 48px;
  height: 48px;
  mask-image: url('${navigateLeftIcon}');
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #3a3a3a;
  margin-right: 12px;
`;

const Next = styled.div<{ disabled?: boolean }>`
  width: 48px;
  height: 48px;
  mask-image: url('${navigateRightIcon}');
  mask-size: contain;
  mask-repeat: no-repeat;
  background-color: #3a3a3a;
  margin-left: 12px;
  opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const ReportWrapper = styled.div`
  flex: 1;
  height: calc(100vh - 128px);
  overflow: auto;

  ${handyBreakpoint} {
    height: auto;
    overflow: visible;
  }
`;

const MobileDateInput = styled.div`
  display: none;

  ${handyBreakpoint} {
    display: block;
  }
`;

const LargeCalendarWrapper = styled.div`
  display: block;

  ${handyBreakpoint} {
    display: none;
  }
`;

const Wrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-flow: row;

  ${handyBreakpoint} {
    flex-flow: column;
    padding-bottom: 40px;
  }
`;

const HeadingWrapper = styled.div`
  ${handyBreakpoint} {
    padding: 0 16px;
    box-sizing: border-box;
  }
`;

const ZReports: FC = () => {
  const [, , venueId] = location.hash.split('/');

  const { i18n, locale } = useI18n();
  const currency = useCurrency();
  const logger = useLogger('z-reports');
  const cashRegisterActions = useCashRegister();
  const licenses = useLicenses();
  const metadata = useMetadata();
  const [storage] = useStorage();
  const bongQuery = useBongQuery(venueId);

  const dates = useDates();
  const currentDate = new Date();

  const [isOngoingDay, setIsOngoingDay] = useState<boolean>(true);
  const [chosenDate, setChosenDate] = useState<Date>(currentDate);
  const [reportDate, setReportDate] = useState<string>('');
  const [showVenueReport, setShowVenueReport] = useState<boolean>(false);
  const [deviceConfigured, setDeviceConfigured] = useState<boolean>(false);
  const [cashRegister, setCashRegister] = useState<CashRegisterTotals>({
    cashInitial: 0,
    cashSalesIncTips: 0,
    cashDeposits: 0,
    cashWithdrawals: 0,
    cashFinal: 0,
  });
  const [payouts, setPayouts] = useState<{ [key: string]: number }>({});

  const companyId = useSelector((state) => state.company);
  const language = useSelector((state) => state.language);
  const companyTimeZone = useSelector((state) => state.companyTimeZone);
  const flags = useSelector((state) => state.companyFlags);
  const country = useSelector((state) => state.country);
  const companyName = useSelector((state) => state.companyName);
  const defaultLanguage = useSelector((state) => state.defaultLanguage);
  const companyStaff = useSelector((state) => state.companyStaff)?.staffs;

  const deviceName = storage.taxComplianceDeviceName || 'Unknown device';

  const checkIfOngoingDate = (date: DateTime) => {
    const now = DateTime.now();
    const isSameDay = date.hasSame(now, 'day');
    const isYesterday = date.hasSame(now.minus({ days: 1 }), 'day');
    const isWithinTimeRange = dates.isBetweenMidnightAnd530(now);

    // If it is today OR yesterday and now is before 5:30
    if (isSameDay || (isYesterday && isWithinTimeRange)) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (!chosenDate) {
      return;
    }
    // If the chosen date is the same as the current date OR the current day is between 00-05:30 AND the current day minus 1 is the same
    setIsOngoingDay(checkIfOngoingDate(DateTime.fromJSDate(chosenDate)));
  }, [chosenDate]);

  let lang: string = language;
  if (language == 'se') lang = 'sv';

  useEffect(() => {
    cashRegisterActions.getLatestDataForDate(chosenDate.toISOString()).then((data) => {
      setCashRegister({
        cashInitial: data?.cashInitial || 0,
        cashSalesIncTips: data?.cashSalesIncTips || 0,
        cashDeposits: data?.cashDeposits || 0,
        cashWithdrawals: data?.cashWithdrawals || 0,
        cashFinal: data?.cashFinal || 0,
      });
    });
  }, [chosenDate]);

  const [configureVenueDevice, { loading: configureVenueDeviceLoading }] = useMutation(
    CONFIGURE_VENUE_DEVICE,
    {
      variables: {
        venueId,
        companyId,
        deviceName,
      },
    },
  );

  const deviceQuery = useQuery<GetDeviceReportSnapshot, GetDeviceReportSnapshotVariables>(
    DEVICE_REPORT_SNAPSHOT_QUERY,
    {
      variables: {
        deviceName,
        venueId,
        date: reportDate,
      },
      fetchPolicy: 'network-only',
      skip: showVenueReport || !reportDate || !deviceConfigured || !isOngoingDay,
    },
  );

  const zReportQuery = useQuery<GetZReports, GetZReportsVariables>(Z_REPORT_QUERY, {
    variables: {
      deviceName: !showVenueReport ? deviceName : undefined,
      venueId,
      date: reportDate,
      timezone: companyTimeZone,
    },
    fetchPolicy: 'network-only',
    skip: !reportDate || !deviceConfigured,
  });

  const cashQuery = useQuery<GetAllCashEvents, GetAllCashEventsVariables>(ALL_CASH_EVENTS_QUERY, {
    variables: {
      filter: { venue: { id: venueId } },
      sentAfter: dates.getAccountingStartDate(chosenDate),
      sentBefore: dates.getAccountingEndDate(chosenDate),
      venueId,
    },
    fetchPolicy: 'network-only',
    skip: !venueId,
  });

  const tableSnapshotQuery = useQuery<
    GetVenueTableEventsSnapshots,
    GetVenueTableEventsSnapshotsVariables
  >(TABLE_EVENTS_SNAPSHOTS_QUERY, {
    variables: {
      venueId: venueId,
      date: dates.getAccountingEndDate(chosenDate), // What tables looked like at the end of the day
    },
    skip: !venueId || !licenses.hasTSR,
    fetchPolicy: 'network-only',
  });

  /* We have fetched all cash events from all pos devices for this venue, filter out per device if selected */
  useEffect(() => {
    if (!cashQuery.loading && cashQuery.data) {
      const allDevices = cashQuery.data.allPOSDevices || [];

      /* If we only should get events from one or not */
      const filteredDevices = showVenueReport
        ? allDevices
        : allDevices.filter((device: any) => device.id === storage.taxComplianceDeviceId);

      const totals = filteredDevices.reduce(
        (acc: CashRegisterTotals, device: any) => {
          const deviceEvents = device.cashEvents?.events?.items || [];

          const {
            cashInitial,
            cashSalesIncTips,
            cashDeposits,
            cashWithdrawals,
            cashFinal,
          } = processCashEvents(deviceEvents);

          return {
            cashInitial: acc.cashInitial + cashInitial,
            cashSalesIncTips: acc.cashSalesIncTips + cashSalesIncTips,
            cashDeposits: acc.cashDeposits + cashDeposits,
            cashWithdrawals: acc.cashWithdrawals + cashWithdrawals,
            cashFinal: acc.cashFinal + cashFinal,
          };
        },
        { cashInitial: 0, cashSalesIncTips: 0, cashDeposits: 0, cashWithdrawals: 0, cashFinal: 0 },
      );

      setCashRegister(totals);
    }
  }, [cashQuery.data, cashQuery.loading, showVenueReport]);

  useEffect(() => {
    if (!cashQuery.loading && cashQuery.data) {
      const allDevices = cashQuery.data.allPOSDevices || [];

      // Filter devices based on showVenueReport
      const filteredDevices = showVenueReport
        ? allDevices
        : allDevices.filter((device: any) => device.id === storage.taxComplianceDeviceId);

      // Parse payouts for all filtered devices
      const parsedPayouts = getParsedPayouts(filteredDevices);
      setPayouts(parsedPayouts);
    }
  }, [cashQuery.data, cashQuery.loading, showVenueReport]);

  const getParsedPayouts = (devices: any[]) => {
    const result: { [key: string]: number } = {};

    devices.forEach((device) => {
      device?.cashEvents?.events?.items
        .filter((event: any) => event.type === 'CASH_CHANGE' && event.amount < 0 && event.sourceId)
        .forEach((event: any) => {
          const sourceName =
            metadata.data.cashSources.find((s) => s.id === event.sourceId)?.name || 'DRAWER';
          result[sourceName] = (result[sourceName] || 0) + -event.amount;
        });
    });

    return result;
  };

  /**
   * Z-reports are historic reports that are closed for new data.
   * X-reports are current reports that are not yet closed.
   * When we are on the current day we want to show a combination of
   * any eventual historic reports (Z) and the current report (X)
   */
  const xReport = showVenueReport ? null : deviceQuery.data?.reportDeviceSnapshot.snapshot;
  const xReportLoading = showVenueReport ? bongQuery.loading : deviceQuery.loading;

  const zReports = zReportQuery.data?.getZReports.success
    ? zReportQuery.data.getZReports.reports
    : null;

  // If the company uses manual end of day, we need to check if the day has started otherwise the day is always considered started
  const dayHasStarted = flags.manualEndOfDay ? xReport?.dayStarted : true;

  // If a device has not been used for a while, the snapshot might be for a completely different day so we need to check if the snapshot is for the current day
  const deviceHasSalesToday =
    xReport && xReport.date && checkIfOngoingDate(DateTime.fromJSDate(new Date(xReport.date)));

  // If the current day has started and we are on the current day, we should show the X-report
  const shouldShowXReport = isOngoingDay && dayHasStarted && deviceHasSalesToday;

  // If the company has manual end of day if they open and close on the same day we need to show multiple reports. There are always at least one Z-report for a day, we only want to show multiple if there are more than one
  const shouldShowMultipleReports =
    flags.manualEndOfDay && xReport?.dayStarted && zReports && zReports.length > 1;

  let reports: any = null;
  const formattedZReports = zReports?.map((report: ZReport) => {
    return {
      ...report,
      isZReport: true,
    };
  });

  if (showVenueReport) {
    // If we are showing the venue report we only want to show the Z-reports
    reports = formattedZReports;
  } else if (shouldShowXReport && shouldShowMultipleReports) {
    const xReportDate: string = xReport.date;

    // There is always a z-report that is a copy of the x-report so we filter it out
    const filteredZReports =
      formattedZReports?.filter((report: ExtendedReport) => {
        return report.date !== xReportDate;
      }) ?? [];

    reports = [...filteredZReports, { ...xReport, isZReport: false }];
  } else if (shouldShowXReport) {
    reports = [xReport];
  } else if (!isOngoingDay) {
    reports = formattedZReports;
  }

  const loading = isOngoingDay
    ? xReportLoading || zReportQuery.loading || cashQuery.loading
    : zReportQuery.loading || cashQuery.loading || tableSnapshotQuery.loading;

  const cheques =
    (tableSnapshotQuery.data?.Venue?.eventsSnapshot?.cheques as Cheque[] | undefined) ?? [];

  /** If user has TSR --> Go through all cheques to sum them up with count & amount */
  const openCheques: CountAndAmount | null = licenses.hasTSR
    ? cheques
        // Filter out cheques created in the POS mode in handy
        .filter((c) => !c.tableId?.startsWith('POS_'))
        .filter((c) => c.tableId !== PARKED_ORDER_TABLE_ID) // Filter out cheques that is parked orders
        .reduce(
          (acc, c) => ({
            count: acc.count + 1,
            amount: acc.amount + (c.amount - c.discount - c.paidAmount),
          }),
          { count: 0, amount: 0 },
        )
    : null;

  useEffect(() => {
    if (!companyId || deviceConfigured || configureVenueDeviceLoading) {
      return;
    }

    configureVenueDevice().then(() => {
      setDeviceConfigured(true);
    });
  }, [companyId]);

  useEffect(() => {
    if (deviceQuery.data?.reportDeviceSnapshot.isPartial) {
      deviceQuery.refetch();
    }
  }, [deviceQuery.data]);

  useEffect(() => {
    const now = DateTime.now();

    if (isOngoingDay) {
      if (showVenueReport) {
        // If we are on the ongoing day and we want to look at the whole venue the date needs to be the accounting start date
        const isBetweenMidnight = dates.isBetweenMidnightAnd530(now);
        if (isBetweenMidnight) {
          const accountingStartDate = dates.getAccountingStartDate(
            now.minus({ days: 1 }).toJSDate(),
          );
          setReportDate(accountingStartDate);
        } else {
          const accountingStartDate = dates.getAccountingStartDate(now.toJSDate());
          setReportDate(accountingStartDate);
        }
      } else {
        setReportDate(now.toJSDate().toISOString());
      }
    } else {
      const accountingStartDate = dates.getAccountingStartDate(chosenDate);
      setReportDate(accountingStartDate);
    }
  }, [chosenDate, isOngoingDay, showVenueReport]);

  const handlePrint = (report: ExtendedReport) => {
    const props = {
      reportTitle: isOngoingDay ? i18n('Report.XReport') : i18n('Report.ZReport'),
      venueName: metadata.data.venueName,
      venueLocation: {
        city: metadata.data.city,
        street: metadata.data.street,
        zip: metadata.data.zip,
      },
      venueOrg: metadata.data.organizationId,
      companyInfo: {
        name: companyName,
        country,
        defaultLanguage,
      },
      taxComplianceDeviceName: deviceName,
      reportDate: !report.isZReport
        ? DateTime.now().toFormat('yyyy–MM–dd HH:mm:ss') // If is is an ongoing day it
        : DateTime.fromISO(report.date || '')
            .setZone(companyTimeZone)
            .toFormat('yyyy–MM–dd HH:mm:ss'),
      data: report,
      cashRegister,
      i18n,
      formatCurrency: currency.format,
      categories: bongQuery.data?.company.categories ?? [],
      staffs: companyStaff ?? [],
      payouts,
      openCheques: showVenueReport ? openCheques : null,
    };

    if (country === 'NO') {
      printNorwayZReport({
        ...props,
      });
    } else {
      printGeneralZReport({
        ...props,
      });
    }

    logger.info('Z/X-report: Printing report', {
      reportData: report,
      cashRegister,
      props,
    });
  };

  const prevButton = useButton({
    onClick: () => {
      const prev = DateTime.fromJSDate(chosenDate).minus({ days: 1 });
      setChosenDate(prev.toJSDate());
    },
  });

  const nextButton = useButton({
    onClick: () => {
      const next = DateTime.fromJSDate(chosenDate).plus({ days: 1 });
      setChosenDate(next.toJSDate());
    },
    disabled: DateTime.fromJSDate(chosenDate).hasSame(DateTime.fromJSDate(currentDate), 'day'),
  });

  // Categories with names translated to the report language, as expected by the send report mutation
  const reportCategories = (bongQuery.data?.company.categories ?? []).map((category) => ({
    id: category.id,
    name: i18n(category.nameLang),
  }));

  const viewProps = {
    data: reports,
    orgId: metadata.data.organizationId,
    companyName,
    country,
    handlePrint,
    cashRegister,
    showVenueReport,
    isXReport: xReport?.dayStarted,
    categories: bongQuery.data?.company.categories ?? [],
    staffs: companyStaff ?? [],
    payouts,
    openCheques: showVenueReport ? openCheques : null,
  };

  const isHandyBreakpoint = window.innerWidth <= 600;

  return (
    <div>
      {!isHandyBreakpoint && <BackTopBar title={i18n('Report.ZReports')} />}

      <Wrapper>
        <CalendarWrapper>
          <MobileDateInput>
            <DateInput
              selectedDate={chosenDate ? new Date(chosenDate) : null}
              setSelectedDate={(date: any) => {
                if (date instanceof Date) {
                  setChosenDate(date);
                }
              }}
              isDark
              maxDate={new Date()}
            />
          </MobileDateInput>
          <LargeCalendarWrapper>
            <Calendar
              onChange={(date: any) => setChosenDate(date)}
              value={chosenDate}
              locale={lang}
              maxDate={currentDate}
            />
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="medium">
              <Prev {...prevButton} />
              <Heading level={3}>{DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd')}</Heading>
              <Next {...nextButton} />
            </Box>
            <SendZReportEmail
              venueId={venueId}
              reportDay={dates.getAccountingStartDate(chosenDate)} // Always matches the selected calendar day
              orgNumber={metadata.data.organizationId}
              categories={reportCategories}
              timezone={companyTimeZone}
              language={locale}
              currency={currency.code}
              // Emailing is only allowed for closed days with a Z-report, never the ongoing day,
              // and requires the venue to have an organization number for the report header
              disabledReason={
                !metadata.data.organizationId
                  ? i18n('Report.EmailDisabledNoOrgNumber')
                  : isOngoingDay
                  ? i18n('Report.EmailDisabledOngoingDay')
                  : !zReports || zReports.length === 0
                  ? i18n('Report.EmailDisabledNoReport')
                  : undefined
              }
            />
          </LargeCalendarWrapper>
          <Box marginTop="large">
            <Toggle
              label={i18n('Report.ShowFullVenueReport')}
              value={showVenueReport}
              onClick={() => setShowVenueReport(!showVenueReport)}
            />
          </Box>
        </CalendarWrapper>
        <ReportWrapper>
          {reports && reports.length === 1 && (
            <HeadingWrapper>
              <Heading
                level={2}
                top={16}
                color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
                {isOngoingDay
                  ? i18n('Report.XReport') + ' ' + DateTime.now().toFormat('yyyy-MM-dd HH:mm')
                  : i18n('Report.ZReport') +
                    ' ' +
                    DateTime.fromJSDate(chosenDate).toFormat('yyyy-MM-dd')}
              </Heading>
              {isOngoingDay && dates.isBetweenMidnightAnd530(DateTime.now()) && (
                <Paragraph
                  level={4}
                  color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
                  This report shows the sales starting at{' '}
                  {DateTime.now()
                    .minus({ days: 1 })
                    .toFormat('yyyy-MM-dd')}{' '}
                  05:30 until {DateTime.now().toFormat('yyyy-MM-dd HH:mm')}
                </Paragraph>
              )}
            </HeadingWrapper>
          )}
          {loading ? (
            <Box
              style={{ height: '50vh' }}
              display="flex"
              alignItems="center"
              flexDirection="column"
              gap="small"
              justifyContent="center">
              <Loader
                color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}
              />
              <Paragraph
                level={3}
                color={isHandyBreakpoint ? 'var(--colorWhite)' : 'var(--colorBlueGray1300)'}>
                {i18n('Report.Loading')}
              </Paragraph>
            </Box>
          ) : country === 'NO' ? (
            <NorwayView {...viewProps} />
          ) : (
            <GeneralView {...viewProps} />
          )}
        </ReportWrapper>
      </Wrapper>
    </div>
  );
};

export default ZReports;
